import { disableSubmitButton, enableSubmitButton } from "../render";
import { initValidation } from "../validation";

export const AutoComplete = async (formDiv : Element, autocompleteInput : HTMLInputElement) => {
  let autocomplete = null;
  let place = null;
  let postalCode = null;
  let country = null;

  const initGeocodeSearch = async () => {
    if (typeof google !== 'object' || typeof google.maps !== 'object') {
      console.log('Waiting for Places API to load...');
      setTimeout(initGeocodeSearch, 500);
    } else {
      autocomplete = await initAutocomplete();
      autocomplete.addListener('place_changed', placeChanged);
      lookupLocation();
    }
  }

  const lookupLocation = async () => {
    const { coords } = await getUserLocation();
    const latLng = {
      lat: coords.latitude,
      lng: coords.longitude,
    };
    // only autofill if the postal code field is empty
    if (autocompleteInput.value == '') {
      const autofillArgs = await autofillFromUserLocation(latLng);
      placeChanged(autofillArgs);
    }
  }

  const autofillFromUserLocation = async (latLng) => {
    const result = await reverseGeocode(latLng);
    const elem = document.getElementsByClassName('postal-code-info');
    const codes = [];
    codes.push(elem[0]);
  
    return {
      fieldset: elem[0],
      address_components: result.address_components,
    };
  };

  const reverseGeocode = (latLng) => new Promise((resolve, reject) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === 'OK') {
        if (results[1]) {
          return resolve(results[1]);
        }
      }
  
      return reject(new Error(`Couldnt't find the location ${status}`));
    });
  });

  const getUserLocation = (options?: PositionOptions) : Promise<GeolocationPosition> => new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(resolve, reject, options);
    } else {
      reject(new Error('User does not have location services enabled.'));
    }
  });

  const initAutocomplete = async (latLng = null) => {
    const countriesAttr = autocompleteInput.getAttribute('data-countries') ?? '';
    const countries = countriesAttr.split(',').filter(country => country.trim() !== '');

    const autocompleteSettings = {
      types: ['(regions)']
    };
    if(countries.length > 0) {
      autocompleteSettings.componentRestrictions = { country: countries };
    }
    
    const autocomplete = new google.maps.places.Autocomplete(autocompleteInput, autocompleteSettings);
  
    if (latLng) {
      setBoundsFromLatLng(autocomplete, latLng);
    }
  
    return autocomplete;
  };

  const setBoundsFromLatLng = (autocomplete, latLng) => {
    const circle = new google.maps.Circle({
      radius: 40233.6, // 25 mi radius
      center: latLng,
    });
    autocomplete.setBounds(circle.getBounds());
  };

  const updateFieldValue = (inputName : string, value : string) => {
    const form = formDiv.querySelector('form');
    const input = form.querySelector(`input[name="${inputName}"]`);
    
    // If input exists, then set value. Otherwise, create the input for Gravity Forms consumption.
    if(input) {
      input.setAttribute('value', value);
    } else {
      const input = document.createElement('input');
      input.setAttribute('type', 'hidden');
      input.setAttribute('name', inputName);
      input.setAttribute('value', value);
      form.appendChild(input);
    }
  }

  const placeChanged = (args) => {
    if (!autocompleteInput.value && typeof args === 'undefined') {
      return;
    }

    // Disable the submit button until all of the JS has processed to prevent sending entries with incorrect geocoding data.
    disableSubmitButton(formDiv);

    if (args) {
      place = args;
    } else {
      place = autocomplete.getPlace();
    }

    for (let i = 0; i < place.address_components.length; i++) {
      const component = place.address_components[i];
      const addressType = component.types[0];

      switch (addressType) {
        case 'postal_code':
          updateFieldValue(autocompleteInput.name + '_zip', component.long_name);
          autocompleteInput.value = component.long_name;
          break;
        case 'country':
          updateFieldValue(autocompleteInput.name + '_country', component.short_name);
          break;
        case 'locality':
          updateFieldValue(autocompleteInput.name + '_city', component.short_name);
          break;
        case 'administrative_area_level_1':
          updateFieldValue(autocompleteInput.name + '_state', component.short_name);
          break;
        default:
          break;
      }
    }

    // Rerun the validation.
    const form = formDiv.querySelector('form');
    const zipFields = form.querySelectorAll('.gfield.gfield--type-zip-to-market-text input');
    const bouncer = initValidation(form);
    zipFields.forEach((field) => {
      bouncer.validate(field);
    });

    enableSubmitButton(formDiv);
  }

  // Initiate the function.
  await initGeocodeSearch();

  // Return the key information for consumption, if needed.
  return {
    postalCode,
    country
  }
}